(() => {
  if (customElements.get('video-showcase-item')) return;

  // Loads the official YouTube IFrame Player API once and shares the
  // promise across every <video-showcase-item> on the page.
  let apiPromise = null;
  function loadYouTubeAPI() {
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
    if (apiPromise) return apiPromise;

    apiPromise = new Promise((resolve) => {
      const previousCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previousCallback === 'function') previousCallback();
        resolve(window.YT);
      };

      if (!document.querySelector('script[data-video-showcase-api]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.dataset.videoShowcaseApi = 'true';
        document.head.appendChild(script);
      }
    });

    return apiPromise;
  }

  // Best-effort attempt to force captions off. cc_load_policy only *forces*
  // captions ON when set to 1 — set to 0 (or omitted) it just falls back to
  // the viewer's own YouTube caption preference, which can still show
  // captions regardless of our setting. Explicitly unloading the captions
  // module is the practical workaround; the module has been named both
  // "captions" and "cc" across YouTube player versions, and can reload
  // asynchronously, so we retry a couple of times.
  function disableCaptions(player) {
    ['captions', 'cc'].forEach((moduleName) => {
      if (typeof player.unloadModule === 'function') {
        try {
          player.unloadModule(moduleName);
        } catch (error) {
          /* undocumented API — fail silently if unavailable */
        }
      }
    });
    if (typeof player.setOption === 'function') {
      try {
        player.setOption('captions', 'track', {});
      } catch (error) {
        /* no-op */
      }
    }
  }

  class VideoShowcaseItem extends HTMLElement {
    connectedCallback() {
      this.videoId = this.dataset.videoId;
      if (!this.videoId) return;

      this.frameWrapper = this.querySelector('.video-showcase__frame-wrapper');

      this.player = null;
      this.playerReady = false;
      this._creatingPlayer = false;

      // Lazily create the real YouTube player once the video is about to
      // scroll into view (so we're not loading three players up front),
      // and pause playback again once it's scrolled back out of view.
      // There's no custom play button of ours — YouTube's own thumbnail
      // and play button, rendered inside the player itself, is the only
      // one a visitor ever sees or clicks.
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries) => entries.forEach((entry) => this._handleIntersect(entry)),
          { threshold: 0, rootMargin: '300px 0px' }
        );
        this._observer.observe(this);
      } else {
        this._ensurePlayer();
      }
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }

    _ensurePlayer() {
      if (this.player || this._creatingPlayer || !this.frameWrapper) return;
      this._creatingPlayer = true;

      const mount = document.createElement('div');
      this.frameWrapper.appendChild(mount);

      loadYouTubeAPI().then((YT) => {
        this.player = new YT.Player(mount, {
          videoId: this.videoId,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 0,
            mute: 0,
            controls: 1,
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
            cc_load_policy: 0,
            iv_load_policy: 3,
            origin: window.location.origin,
          },
          events: {
            onReady: (event) => this._handleReady(event),
            onStateChange: (event) => this._handleStateChange(event),
          },
        });
      });
    }

    _handleReady(event) {
      this.playerReady = true;
      const player = event.target;

      player.setVolume(50);
      disableCaptions(player);
      // Captions can finish loading a moment after onReady fires, so make
      // a second attempt shortly after.
      window.setTimeout(() => disableCaptions(player), 500);

      const iframeEl = typeof player.getIframe === 'function' ? player.getIframe() : null;
      if (iframeEl) {
        iframeEl.style.width = '100%';
        iframeEl.style.height = '100%';
        iframeEl.style.border = '0';
        iframeEl.style.display = 'block';
      }

      this.classList.add('is-loaded');
    }

    _handleStateChange(event) {
      // 1 = YT.PlayerState.PLAYING
      if (event.data === 1) {
        disableCaptions(event.target);
      }
    }

    _handleIntersect(entry) {
      if (entry.isIntersecting) {
        this._ensurePlayer();
        return;
      }
      if (this.playerReady && this.player && typeof this.player.pauseVideo === 'function') {
        this.player.pauseVideo();
      }
    }
  }

  customElements.define('video-showcase-item', VideoShowcaseItem);
})();
